import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { footerItems, BizcoverIndiaBrandMark } from "@b2b/ui-components";
import {
  ChevronRight,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

import { FaTwitter, FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";


const BODY_FONT = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif',
} as const;
const TITLE_FONT = BODY_FONT;

export type BizcoverIndiaFigmaFooterProps = {
  email?: string;
  onEmailChange?: (value: string) => void;
  onSubscribe?: (email: string) => void;
};

function FooterList({
  title,
  items,
}: {
  title: string;
  items: readonly { label: string; href: string }[];
}) {
  const isExternalHref = (href: string) =>
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:");

  return (
    <div className="min-w-0 text-[#DCEBFF]" style={BODY_FONT}>
      <h4 className="mb-3 text-[22px] font-bold leading-[28px] sm:mb-4 sm:text-[26px] sm:leading-[32px]" style={TITLE_FONT}>
        {title}
      </h4>
      <div className="mb-3 h-px w-full bg-[rgba(145,158,171,0.24)]" />
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={`${title}-${item.label}`} className="flex items-start gap-2 py-1 text-[13px] font-semibold leading-6 sm:text-[14px]">
            <span className="inline-flex shrink-0">
              <ChevronRight className="h-3.5 w-3.5 text-[#DCEBFF]" />
            </span>
            {isExternalHref(item.href) ? (
              <a
                href={item.href}
                className="bz-footer-link block min-w-0 flex-1 whitespace-normal break-words"
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                {item.label}
              </a>
            ) : (
              <Link to={item.href} className="bz-footer-link block min-w-0 flex-1 whitespace-normal break-words">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BizcoverIndiaFigmaFooter({
  email: emailProp = "",
  onEmailChange,
  onSubscribe,
}: BizcoverIndiaFigmaFooterProps) {
  const [uncontrolledEmail, setUncontrolledEmail] = useState("");
  const isControlled = onEmailChange != null;
  const email = isControlled ? emailProp : uncontrolledEmail;

  const handleEmailChange = (value: string) => {
    if (isControlled) onEmailChange(value);
    else setUncontrolledEmail(value);
  };

  const marketplaceSection = useMemo(
    () => footerItems.sections.find((section) => section.title === "Marketplace"),
    []
  );
  const supportSection = useMemo(
    () => footerItems.sections.find((section) => section.title === "Support"),
    []
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubscribe?.(email);
  };

  return (
    <footer className="bz-footer relative overflow-hidden text-white" style={BODY_FONT}>
      <style>{`
        .bz-footer {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          letter-spacing: 0;
        }
        .bz-footer h3,
        .bz-footer h4 {
          letter-spacing: -0.01em;
        }
        .bz-footer-link {
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .bz-footer-link:hover {
          color: #FFFFFF;
          transform: translateX(2px);
        }
        .bz-footer-form {
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .bz-footer-form:focus-within {
          border-color: #F97316 !important;
          box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.18);
        }
        .bz-footer-submit {
          position: relative;
          overflow: hidden;
          isolation: isolate;
          transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .bz-footer-submit:hover {
          background-color: #EA580C;
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(249, 115, 22, 0.34);
        }
        .bz-footer-submit::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
          transform: translateX(-120%) skewX(-20deg);
          pointer-events: none;
          z-index: 1;
        }
        .bz-footer-submit:hover::after {
          animation: bz-footer-shine 0.9s ease;
        }
        @keyframes bz-footer-shine {
          0% { transform: translateX(-120%) skewX(-20deg); }
          100% { transform: translateX(220%) skewX(-20deg); }
        }
        .bz-footer-social {
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .bz-footer-social:hover {
          background-color: rgba(249, 115, 22, 0.85) !important;
          transform: translateY(-2px) scale(1.06);
        }
        @media (prefers-reduced-motion: reduce) {
          .bz-footer-link,
          .bz-footer-form,
          .bz-footer-submit,
          .bz-footer-social {
            transition: none;
          }
          .bz-footer-link:hover,
          .bz-footer-submit:hover,
          .bz-footer-social:hover {
            transform: none;
          }
          .bz-footer-submit:hover::after {
            animation: none;
          }
        }
      `}</style>
      <div className="mx-auto max-w-[1920px]">
        <div className="bg-[#1F4E6B] pb-3 pt-4 sm:pb-4 sm:pt-5 lg:pb-5">
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 top-0 z-[0] bg-[#1F4E6B]"
          />
          <div className="relative z-[1] mx-auto grid w-full max-w-[1630px] items-start gap-5 px-4 py-3 sm:px-6 sm:py-4 md:px-8 lg:grid-cols-[minmax(280px,1.15fr)_minmax(0,1fr)] lg:gap-x-8 xl:grid-cols-[minmax(340px,1.05fr)_minmax(0,1fr)_minmax(250px,0.72fr)] xl:gap-x-10">
            <div className="min-w-0">
              <BizcoverIndiaBrandMark variant="footer" to="/" />
              <p className="mt-2 max-w-[460px] text-[13px] leading-5 text-white/90 sm:text-[14px]">
                {footerItems.companyDescription}
              </p>
              <div className="mt-3 flex items-center gap-2.5">
                {[FaTwitter, FaFacebook, FaYoutube, FaInstagram].map((SocialIcon, index) => (
                    <span
                      key={index}
                      className="bz-footer-social inline-flex size-7 items-center justify-center rounded-full bg-[rgba(220,235,255,0.20)] p-1.5"
                    >
                      <a
                        href={footerItems.socialLinks[index]?.href || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={footerItems.socialLinks[index]?.label || "Social"}
                        className="inline-flex size-full items-center justify-center"
                      >
                        <SocialIcon className="h-4 w-4 text-white" />
                      </a>
                    </span>
                  ))}
              </div>
              <div className="mt-4 w-full max-w-[min(460px,100%)] rounded-2xl bg-white/10 p-3 backdrop-blur-[2px] sm:p-3.5">
                <h3 className="text-[18px] font-bold leading-[24px] text-white sm:text-[20px] sm:leading-[26px]" style={TITLE_FONT}>
                  Subscribe to the newsletter
                </h3>
                <p className="mt-1 text-[12px] leading-[18px] text-white/80 sm:text-[13px] sm:leading-[20px]">
                  Stay updated! Subscribe to our mailing list for news, updates, and exclusive offers.
                </p>
                <form
                  onSubmit={handleSubmit}
                  className="bz-footer-form mt-2 flex w-full min-w-0 flex-col gap-2 rounded-2xl border border-[#dfe3e8] bg-white p-2 sm:h-11 sm:flex-row sm:items-center sm:gap-2 sm:rounded-[100px] sm:px-3 sm:py-0"
                >
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden self-center">
                    <Mail className="size-5 shrink-0 overflow-hidden text-[#1A2A3D]" />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    placeholder="Enter your email"
                    className="min-w-0 flex-1 border-0 bg-transparent text-[14px] leading-5 text-[#1A2A3D] placeholder:text-[#919eab] focus:outline-none sm:h-9"
                  />
                  <button
                    type="submit"
                    className="bz-footer-submit w-full shrink-0 rounded-xl bg-[#F97316] px-4 py-2 text-[13px] font-semibold leading-5 text-white shadow-[0px_6px_15px_0px_rgba(249,115,22,0.26)] sm:h-8 sm:w-auto sm:rounded-3xl sm:px-4 sm:py-0 sm:leading-5"
                  >
                    Submit
                  </button>
                </form>
              </div>
              
            </div>

            <div className="grid min-w-0 grid-cols-1 items-start gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
              <div className="min-w-0">
                <FooterList
                  title={marketplaceSection?.title || "Marketplace"}
                  items={marketplaceSection?.links || []}
                />
              </div>
              <div className="min-w-0 sm:col-span-1 xl:col-span-1">
                <FooterList title={supportSection?.title || "Support"} items={supportSection?.links || []} />
              </div>
            </div>

            <div className="min-w-0 self-start text-[#DCEBFF] lg:col-span-2 xl:col-span-1">
              <h4 className="mb-3 text-[22px] font-bold leading-[28px] sm:mb-4 sm:text-[26px] sm:leading-[32px]" style={TITLE_FONT}>
                Contact Information&apos;s
              </h4>
              <div className="mb-3 h-px w-full bg-[rgba(145,158,171,0.24)]" />
              <div className="space-y-2">
                {[
                  { Icon: MapPin, text: footerItems.contactInfo.address },
                  { Icon: Phone, text: `Call Us: ${footerItems.contactInfo.phone}` },
                  { Icon: Mail, text: footerItems.contactInfo.email },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-2.5">
                    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[rgba(145,158,171,0.16)] p-1.5">
                      <item.Icon className="h-4 w-4 text-[#DCEBFF]" />
                    </span>
                    <span className="min-w-0 whitespace-normal break-words text-[13px] font-semibold leading-6 sm:text-[14px]">
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative z-[1] mx-auto h-px w-[92%] sm:w-[84%] bg-[rgba(145,158,171,0.24)]" />
          
          <p className="relative z-[1] px-4 py-2.5 text-center text-[12px] text-white sm:text-[13px]">
            {footerItems.copyrightText}
          </p>
        </div>
      </div>
    </footer>
  );
}
